import React, { useCallback, useState } from 'react';
import { ArrowRight, ChevronDown, ChevronUp, Redo2, Undo2 } from 'lucide-react';
import AutoExpandingTextarea from 'components/atomic/molecules/auto-expanding-textarea';
import RemovableToken from 'components/atomic/atoms/removable-token';
import IconPill from 'components/atomic/atoms/icon-pill';
import ThumbnailPill from 'components/atomic/atoms/thumbnail-pill';
import { buildClassName } from 'utils/build-class-name';
import type { SmartSearchCardProps, SmartSearchSection } from './smart-search-card.types';
import styles from './style.module.scss';

export type {
  SmartSearchCardProps,
  SmartSearchToken,
  SmartSearchSuggestion,
  SmartSearchSection,
  SmartSearchSectionLayout,
} from './smart-search-card.types';

const TOKEN_SHIMMER_WIDTHS = ['84px', '64px', '72px'];

/**
 * SmartSearchCard - Expanded natural-language search surface.
 *
 * Atomic Level: Organism
 *
 * Presentational: the caller owns parsing, history, and submission. Understood
 * parts of the query appear as removable chips above the query box, leftovers
 * as a hint below it, and any number of suggestion sections below that.
 *
 * @example
 * ```tsx
 * <SmartSearchCard
 *   value={query}
 *   onValueChange={setQuery}
 *   onSubmit={apply}
 *   tokens={tokens}
 *   onTokenRemove={removeToken}
 *   sections={[popularAreas, getInspired]}
 * />
 * ```
 */
export function SmartSearchCard({
  value,
  onValueChange,
  onSubmit,
  tokens = [],
  onTokenRemove,
  isLoadingTokens = false,
  onClear,
  sections = [],
  onSuggestionSelect,
  smartTagsDisclaimer,
  locationNote,
  unparsedText,
  placeholder = 'Describe the home you are looking for',
  submitLabel = 'Search',
  isSubmitDisabled = false,
  onUndo,
  onRedo,
  canUndo = false,
  canRedo = false,
  onDismiss,
  autoFocus = false,
  className,
  testId = 'SmartSearchCard',
}: SmartSearchCardProps) {
  const [collapsedSectionIds, setCollapsedSectionIds] = useState<string[]>([]);

  const toggleSection = useCallback((sectionId: string) => {
    setCollapsedSectionIds(current => (
      current.includes(sectionId)
        ? current.filter(id => id !== sectionId)
        : [...current, sectionId]
    ));
  }, []);

  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Escape' && onDismiss) {
      event.preventDefault();
      onDismiss();
    }
  }, [onDismiss]);

  const renderSuggestion = (section: SmartSearchSection, suggestionId: string) => () => {
    onSuggestionSelect?.(section.id, suggestionId);
  };

  const removableTokenCount = tokens.filter(token => token.isRemovable !== false).length;

  return (
    <section className={buildClassName(styles.root, className)} data-testid={testId}>
      {(tokens.length > 0 || isLoadingTokens || onClear) && (
        <div className={styles.tokenRow}>
          {(tokens.length > 0 || isLoadingTokens) && (
            <ul className={styles.tokens} data-testid={`${testId}-Tokens`}>
              {tokens.map(token => (
                <li key={token.id}>
                  <RemovableToken
                    prefix={token.prefix}
                    label={token.label}
                    icon={token.icon}
                    tone={token.tone}
                    onRemove={onTokenRemove && token.isRemovable !== false ? () => onTokenRemove(token.id) : undefined}
                    removeLabel={`Remove ${token.label}`}
                    testId={`${testId}-Token-${token.id}`}
                  />
                </li>
              ))}
              {isLoadingTokens && TOKEN_SHIMMER_WIDTHS.map((width, index) => (
                <li key={`shimmer-${index}`} aria-hidden>
                  <span
                    className={styles.tokenShimmer}
                    style={{ width }}
                    data-testid={`${testId}-TokenShimmer`}
                  />
                </li>
              ))}
            </ul>
          )}
          {onClear && (
            <button
              type="button"
              className={styles.clearButton}
              onClick={onClear}
              data-testid={`${testId}-Clear`}
            >
              {removableTokenCount > 0 ? `Clear (${removableTokenCount})` : 'Clear'}
            </button>
          )}
        </div>
      )}

      <div className={styles.queryBox}>
        <AutoExpandingTextarea
          value={value}
          onValueChange={onValueChange}
          onSubmit={isSubmitDisabled ? undefined : onSubmit}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          ariaLabel="Smart search"
          minRows={3}
          autoFocus={autoFocus}
          testId={`${testId}-Input`}
        />
        <div className={styles.queryActions}>
          <div className={styles.historyActions}>
            {onUndo && (
              <button
                type="button"
                className={styles.iconButton}
                onClick={onUndo}
                disabled={!canUndo}
                aria-label="Undo"
                data-testid={`${testId}-Undo`}
              >
                <Undo2 size={16} strokeWidth={2} aria-hidden />
              </button>
            )}
            {onRedo && (
              <button
                type="button"
                className={styles.iconButton}
                onClick={onRedo}
                disabled={!canRedo}
                aria-label="Redo"
                data-testid={`${testId}-Redo`}
              >
                <Redo2 size={16} strokeWidth={2} aria-hidden />
              </button>
            )}
          </div>
          <button
            type="button"
            className={styles.submitButton}
            onClick={onSubmit}
            disabled={isSubmitDisabled}
            data-testid={`${testId}-Submit`}
          >
            {submitLabel}
            <ArrowRight size={16} strokeWidth={2.5} aria-hidden />
          </button>
        </div>
      </div>

      {unparsedText && (
        <p className={styles.unparsed} data-testid={`${testId}-Unparsed`}>
          {'Not applied as a filter: '}
          <span className={styles.unparsedText}>{unparsedText}</span>
        </p>
      )}

      {(smartTagsDisclaimer || locationNote) && (
        <div className={styles.notes}>
          {smartTagsDisclaimer && (
            <p className={styles.note} data-testid={`${testId}-SmartTagsDisclaimer`}>
              {smartTagsDisclaimer}
            </p>
          )}
          {locationNote && (
            <p className={styles.note} data-testid={`${testId}-LocationNote`}>
              {locationNote}
            </p>
          )}
        </div>
      )}

      {sections.map(section => {
        const isCollapsed = collapsedSectionIds.includes(section.id);
        return (
          <div key={section.id} className={styles.section} data-testid={`${testId}-Section-${section.id}`}>
            <div className={styles.sectionHeader}>
              {section.icon && (
                <span className={styles.sectionIcon} aria-hidden>{section.icon}</span>
              )}
              <h3 className={styles.sectionTitle}>{section.title}</h3>
              <div className={styles.sectionHeaderActions}>
                {section.headerAction}
                {section.isCollapsible && (
                  <button
                    type="button"
                    className={styles.iconButton}
                    onClick={() => toggleSection(section.id)}
                    aria-expanded={!isCollapsed}
                    aria-label={`${isCollapsed ? 'Expand' : 'Collapse'} ${section.title}`}
                    data-testid={`${testId}-Section-${section.id}-Toggle`}
                  >
                    {isCollapsed
                      ? <ChevronDown size={16} strokeWidth={2} aria-hidden />
                      : <ChevronUp size={16} strokeWidth={2} aria-hidden />}
                  </button>
                )}
              </div>
            </div>
            {!isCollapsed && section.layout === 'list' && (
              <ul className={styles.sectionRows}>
                {section.items.map(item => (
                  <li key={item.id}>
                    <button
                      type="button"
                      className={styles.sectionRow}
                      onClick={renderSuggestion(section, item.id)}
                      data-testid={`${testId}-Suggestion-${item.id}`}
                    >
                      {item.icon && (
                        <span className={styles.sectionRowIcon} aria-hidden>{item.icon}</span>
                      )}
                      <span className={styles.sectionRowLabel}>{item.label}</span>
                      {item.caption && (
                        <span className={styles.sectionRowCaption}>{item.caption}</span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {!isCollapsed && section.layout !== 'list' && (
              <div
                className={styles.sectionItems}
                style={{ gridTemplateRows: `repeat(${section.rows ?? 1}, auto)` }}
              >
                {section.items.map(item => (
                  item.imageUrl ? (
                    <ThumbnailPill
                      key={item.id}
                      src={item.imageUrl}
                      label={item.label}
                      caption={item.caption}
                      onClick={onSuggestionSelect ? renderSuggestion(section, item.id) : undefined}
                      testId={`${testId}-Suggestion-${item.id}`}
                    />
                  ) : (
                    <IconPill
                      key={item.id}
                      icon={item.icon}
                      label={item.label}
                      variant={section.pillVariant}
                      size="md"
                      onClick={onSuggestionSelect ? renderSuggestion(section, item.id) : undefined}
                      testId={`${testId}-Suggestion-${item.id}`}
                    />
                  )
                ))}
              </div>
            )}
          </div>
        );
      })}
    </section>
  );
}

export default SmartSearchCard;
